import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { toast } from "sonner";
import { EmptyState, Field, SectionCard } from "./shared";
import { formatCurrency } from "./LineGrid";
import { voucherTypeOptions } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";

const typeToRoute = {
  Receipt: `${ACCOUNTING_BASE}/receipts`,
  Payment: `${ACCOUNTING_BASE}/payments`,
  Contra: `${ACCOUNTING_BASE}/contra`,
  Journal: `${ACCOUNTING_BASE}/journals`,
  Purchase: `${ACCOUNTING_BASE}/vendor-bills`,
};

const ENGINE_VOUCHER_TYPE_MAP = {
  Receipt: "RECEIPT", Payment: "PAYMENT", Contra: "CONTRA", Journal: "JOURNAL",
  Purchase: "PURCHASE", "Credit Note": "CREDIT_NOTE", "Debit Note": "DEBIT_NOTE",
};

const filterBlank = { dateFrom: "", dateTo: "", voucherType: "all", status: "all", ledgerId: "all", vendorId: "all", referenceNo: "", q: "" };

// Payment/Receipt/Contra/Journal/Purchase/Credit Note/Debit Note now all post
// through the Phase 3 engine (/engine/vouchers). Legacy /accounting/entries
// rows (created before this module existed) are merged in read-only so
// historical data is never hidden, but every filter and action below is
// built against the real engine shape.
function engineVoucherToRow(v) {
  const debitLine = v.lines?.find((l) => Number(l.debit) > 0);
  const creditLine = v.lines?.find((l) => Number(l.credit) > 0);
  const label = (line) => line?.bankAccountLabel || line?.vendorLabel || line?.ledgerName || "-";
  return {
    id: v.id, entryDate: v.voucherDate, voucherType: v.voucherType, source: "engine",
    debitLedger: label(debitLine), creditLedger: label(creditLine),
    partyName: v.lines?.find((l) => l.partyName)?.partyName || "",
    entryStatus: v.status, referenceNo: v.referenceNo || v.voucherNumber, amount: v.totalDebit,
    voucherNumber: v.voucherNumber, createdBy: v.createdBy,
  };
}

export default function VouchersPage() {
  const [entries, setEntries] = useState([]);
  const [engineVouchers, setEngineVouchers] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filters, setFilters] = useState(filterBlank);
  const [loading, setLoading] = useState(true);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.set("dateTo", filters.dateTo);
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.voucherType !== "all" && ENGINE_VOUCHER_TYPE_MAP[filters.voucherType]) params.set("voucherType", ENGINE_VOUCHER_TYPE_MAP[filters.voucherType]);
      if (filters.ledgerId !== "all") params.set("ledgerId", filters.ledgerId);
      if (filters.vendorId !== "all") params.set("vendorId", filters.vendorId);
      if (filters.referenceNo.trim()) params.set("referenceNo", filters.referenceNo.trim());
      if (filters.q.trim()) params.set("q", filters.q.trim());

      const [entriesRes, engineRes, ledgersRes, vendorsRes] = await Promise.all([
        api.get("/society/accounting/entries").catch(() => ({ data: [] })),
        api.get(`/society/accounting/engine/vouchers?${params.toString()}`).catch(() => ({ data: [] })),
        api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
        api.get("/society/accounting/engine/vendors").catch(() => ({ data: [] })),
      ]);
      setEntries(Array.isArray(entriesRes.data) ? entriesRes.data : []);
      setEngineVouchers(Array.isArray(engineRes.data) ? engineRes.data : []);
      setLedgers(Array.isArray(ledgersRes.data) ? ledgersRes.data : []);
      setVendors(Array.isArray(vendorsRes.data) ? vendorsRes.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load vouchers"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filters.dateFrom, filters.dateTo, filters.status, filters.voucherType, filters.ledgerId, filters.vendorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const approveEntry = async (entry, accepted) => {
    try {
      await api.patch(`/society/accounting/entries/${entry.id}/approve`, { accepted, remarks: "" });
      toast.success(accepted ? "Entry approved" : "Entry rejected");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update entry approval"));
    }
  };

  const postVoucher = async (row) => {
    try {
      await api.post(`/society/accounting/engine/vouchers/${row.id}/post`);
      toast.success("Voucher posted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not post this voucher"));
    }
  };

  const deleteDraft = async (row) => {
    if (!window.confirm(`Delete draft voucher ${row.referenceNo || ""}? This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${row.id}`);
      toast.success("Draft deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this draft"));
    }
  };

  // Correction pass ("Safe Hard Delete"): a genuine hard delete for an
  // already-POSTED voucher, distinct from Cancel (which keeps full history
  // via an auto-reversal). The backend only allows it when nothing depends
  // on this voucher (see engine_delete_voucher / VoucherViewPage.jsx for
  // the full list of checks) and reports the exact reason if it doesn't.
  const deleteEngineVoucher = async (row) => {
    if (!window.confirm(`Permanently delete ${row.voucherNumber || "this voucher"}? Its accounting entries are removed completely and immediately - unlike Cancel, no reversal record is kept. This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${row.id}`);
      toast.success("Voucher permanently deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this voucher"));
    }
  };

  const cancelEngineVoucher = async (row) => {
    const reason = window.prompt(`Cancel voucher ${row.voucherNumber}? This posts an automatic reversal and cannot be undone. Cancellation reason (required):`, "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A cancellation reason is required"); return; }
    try {
      await api.post(`/society/accounting/engine/vouchers/${row.id}/cancel`, { reason: reason.trim() });
      toast.success("Voucher cancelled and reversed");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not cancel this voucher"));
    }
  };

  // Testing/setup-only escape hatch - see BankVoucherFormPage.jsx's copy of
  // this function and the backend endpoint's docstring for the rationale.
  // Only ever offered for an already-cancelled voucher, never a posted one.
  const forceDeleteEngineVoucher = async (row) => {
    const reason = window.prompt(`Permanently delete ${row.voucherNumber} and its reversal voucher? This is for removing TEST data only and cannot be undone - it is different from Cancel and leaves no trace. Reason (required):`, "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    if (!window.confirm(`Really permanently delete ${row.voucherNumber}? This cannot be undone.`)) return;
    try {
      await api.delete(`/society/accounting/engine/vouchers/${row.id}/force-delete`, { params: { reason: reason.trim() } });
      toast.success("Voucher permanently deleted");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not delete this voucher"));
    }
  };

  const allRows = useMemo(() => {
    const legacyFiltered = filters.status === "all" || filters.status === "posted" ? entries : [];
    return [...legacyFiltered, ...engineVouchers.map(engineVoucherToRow)]
      .sort((a, b) => String(b.entryDate).localeCompare(String(a.entryDate)));
  }, [entries, engineVouchers, filters.status]);

  const filtered = allRows.filter((entry) => {
    if (filters.voucherType !== "all" && String(entry.voucherType || entry.entryType || "").toLowerCase() !== filters.voucherType.toLowerCase()) return false;
    return true;
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-extrabold text-[#111827]">All Vouchers</h2>
          <p className="mt-1 text-sm text-[#6B7280]">Every voucher across all types. Open a specific voucher type to create a new one.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.entries(typeToRoute).map(([type, to]) => (
          <Link key={type} to={to}><Button variant="outline" size="sm">New {type}</Button></Link>
        ))}
      </div>

      <SectionCard title="Filters">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="From"><Input type="date" value={filters.dateFrom} onChange={(e) => setFilter("dateFrom", e.target.value)} /></Field>
          <Field label="To"><Input type="date" value={filters.dateTo} onChange={(e) => setFilter("dateTo", e.target.value)} /></Field>
          <Field label="Voucher Type">
            <Select value={filters.voucherType} onValueChange={(v) => setFilter("voucherType", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {voucherTypeOptions.map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                <SelectItem value="Credit Note">Credit Note</SelectItem>
                <SelectItem value="Debit Note">Debit Note</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status">
            <Select value={filters.status} onValueChange={(v) => setFilter("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Ledger">
            <Select value={filters.ledgerId} onValueChange={(v) => setFilter("ledgerId", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All ledgers</SelectItem>
                {ledgers.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Vendor">
            <Select value={filters.vendorId} onValueChange={(v) => setFilter("vendorId", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All vendors</SelectItem>
                {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.vendorName}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reference">
            <Input value={filters.referenceNo} onChange={(e) => setFilter("referenceNo", e.target.value)} onBlur={load} placeholder="Bill / UTR / cheque no." />
          </Field>
          <Field label="Search voucher number">
            <Input value={filters.q} onChange={(e) => setFilter("q", e.target.value)} onBlur={load} placeholder="e.g. PMT/2026-2027/000012" />
          </Field>
        </div>
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={load}>Apply</Button>
          <Button size="sm" variant="outline" onClick={() => { setFilters(filterBlank); }}>Reset</Button>
        </div>
      </SectionCard>

      <div className="overflow-x-auto rounded-sm border border-[#E5E7EB] bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Voucher</TableHead><TableHead>Debit</TableHead><TableHead>Credit</TableHead>
              <TableHead>Party</TableHead><TableHead>Status</TableHead><TableHead>Reference</TableHead><TableHead>Created By</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="py-8 text-center text-[#6B7280]">Loading...</TableCell></TableRow>
            ) : filtered.slice(0, 100).map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{entry.entryDate}</TableCell>
                <TableCell>{entry.voucherType || entry.entryType}</TableCell>
                <TableCell>{entry.debitLedger || entry.account || "-"}</TableCell>
                <TableCell>{entry.creditLedger || entry.category || "-"}</TableCell>
                <TableCell>{entry.partyName || "-"}</TableCell>
                <TableCell className="capitalize">{entry.entryStatus || "approved"}</TableCell>
                <TableCell className="font-mono text-xs">{entry.referenceNo || "-"}</TableCell>
                <TableCell>{entry.createdBy || "-"}</TableCell>
                <TableCell className="text-right">{formatCurrency(entry.amount)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-wrap justify-end gap-2 text-xs font-semibold">
                    {entry.source === "engine" ? (
                      <>
                        <Link to={`${ACCOUNTING_BASE}/vouchers/${entry.id}`} className="text-[#111827] hover:underline">View</Link>
                        {entry.entryStatus === "draft" && (
                          <>
                            <Link to={`${ACCOUNTING_BASE}/vouchers/${entry.id}/edit`} className="text-[#111827] hover:underline">Edit</Link>
                            <button type="button" onClick={() => postVoucher(entry)} className="text-[#111827] hover:underline">Post</button>
                            <button type="button" onClick={() => deleteDraft(entry)} className="text-red-600 hover:underline">Delete</button>
                          </>
                        )}
                        {entry.entryStatus === "posted" && (
                          <>
                            <Link to={`${ACCOUNTING_BASE}/vouchers/${entry.id}/edit`} className="text-[#111827] hover:underline">Edit</Link>
                            <button type="button" onClick={() => deleteEngineVoucher(entry)} className="text-red-600 hover:underline">Delete</button>
                            {/* Phase 13 (Journal/Contra correction), item 3: Contra and
                                Journal no longer have any Draft/Post/Cancel lifecycle -
                                Edit/Delete are the complete action set for them here too.
                                Every other voucher type keeps Cancel unchanged. */}
                            {!["CONTRA", "JOURNAL"].includes(entry.voucherType) && (
                              <button type="button" onClick={() => cancelEngineVoucher(entry)} className="text-red-600 hover:underline">Cancel</button>
                            )}
                          </>
                        )}
                        {entry.entryStatus === "cancelled" && (
                          <button type="button" onClick={() => forceDeleteEngineVoucher(entry)} className="text-red-600 hover:underline" title="Testing only: permanently removes this voucher and its reversal">Force delete</button>
                        )}
                      </>
                    ) : entry.entryStatus === "pending_approval" ? (
                      <>
                        <Button size="sm" onClick={() => approveEntry(entry, true)} className="bg-emerald-600 text-white hover:bg-emerald-700">Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => approveEntry(entry, false)}>Reject</Button>
                      </>
                    ) : "-"}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={10} className="py-0"><div className="py-8"><EmptyState>No vouchers match this filter. Create your first voucher from one of the buttons above.</EmptyState></div></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
